/** Native polygon geometry, shared by persistence, quantities and viewport editing. */
export interface Point { x: number; y: number }
export interface PolygonGeometry {
  type: "poly";
  vertices: Point[];
  holes?: Point[][];
}

export function polygonRings(geometry: Pick<PolygonGeometry, "vertices" | "holes">): Point[][] {
  return [geometry.vertices, ...(geometry.holes ?? [])];
}

export function mapPolygonPoints(
  geometry: PolygonGeometry,
  transform: (point: Point, index: number) => Point,
): PolygonGeometry {
  let index = 0;
  const rings = polygonRings(geometry).map(ring => ring.map(point => transform(point, index++)));
  return { ...geometry, vertices: rings[0], ...(geometry.holes ? { holes: rings.slice(1) } : {}) };
}

export function replacePolygonRing(geometry: PolygonGeometry, ringIndex: number, points: Point[]): PolygonGeometry {
  if (ringIndex === 0) return { ...geometry, vertices: points };
  return { ...geometry, holes: geometry.holes?.map((ring, index) => index === ringIndex - 1 ? points : ring) };
}

export function polygonVertexLocation(geometry: PolygonGeometry, index: number) {
  const rings = polygonRings(geometry);
  for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
    if (index >= 0 && index < rings[ringIndex].length) return { ringIndex, vertexIndex: index, ring: rings[ringIndex] };
    index -= rings[ringIndex].length;
  }
  return null;
}

export function polygonRingArea(points: Point[]): number {
  return Math.abs(points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0)) / 2;
}

function cross(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function onSegment(a: Point, b: Point, p: Point): boolean {
  return Math.abs(cross(a, b, p)) < 1e-12 &&
    p.x >= Math.min(a.x, b.x) && p.x <= Math.max(a.x, b.x) &&
    p.y >= Math.min(a.y, b.y) && p.y <= Math.max(a.y, b.y);
}

function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  if (onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b)) return true;
  return cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0;
}

export function pointInPolygonRing(point: Point, ring: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.y > point.y) !== (b.y > point.y) &&
      point.x < (b.x - a.x) * (point.y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Positive filled-surface overlap; shared edges and space inside a cutout are disjoint. */
export function polygonSurfacesOverlap(a: PolygonGeometry, b: PolygonGeometry): boolean {
  const bounds = (g: PolygonGeometry) => ({
    left: Math.min(...g.vertices.map(p => p.x)), right: Math.max(...g.vertices.map(p => p.x)),
    top: Math.min(...g.vertices.map(p => p.y)), bottom: Math.max(...g.vertices.map(p => p.y)),
  });
  const ba = bounds(a), bb = bounds(b);
  if (ba.right <= bb.left || bb.right <= ba.left || ba.bottom <= bb.top || bb.bottom <= ba.top) return false;
  const edges = (g: PolygonGeometry) => polygonRings(g).flatMap((ring, index) => {
    const signedArea = ring.reduce((sum, p, i) => {
      const q = ring[(i + 1) % ring.length];
      return sum + p.x * q.y - q.x * p.y;
    }, 0);
    const filledSide = Math.sign(signedArea) * (index === 0 ? 1 : -1);
    return ring.map((p, i) => ({ p, q: ring[(i + 1) % ring.length], filledSide }));
  });
  const ea = edges(a), eb = edges(b);
  const inside = (p: Point, g: PolygonGeometry, boundary: ReturnType<typeof edges>) =>
    !boundary.some(e => onSegment(e.p, e.q, p)) && pointInPolygonRing(p, g.vertices) &&
    !(g.holes ?? []).some(hole => pointInPolygonRing(p, hole));
  for (const [own, other, otherEdges] of [[ea, b, eb], [eb, a, ea]] as const) {
    if (own.some(({ p, q }) => inside(p, other, otherEdges) ||
      inside({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, other, otherEdges))) return true;
  }
  return ea.some(u => eb.some(v => {
    if (cross(u.p, u.q, v.p) * cross(u.p, u.q, v.q) < 0 &&
      cross(v.p, v.q, u.p) * cross(v.p, v.q, u.q) < 0) return true;
    if (Math.abs(cross(u.p, u.q, v.p)) >= 1e-12 || Math.abs(cross(u.p, u.q, v.q)) >= 1e-12) return false;
    const axis = Math.abs(u.q.x - u.p.x) >= Math.abs(u.q.y - u.p.y) ? "x" : "y";
    const span = Math.min(Math.max(u.p[axis], u.q[axis]), Math.max(v.p[axis], v.q[axis])) -
      Math.max(Math.min(u.p[axis], u.q[axis]), Math.min(v.p[axis], v.q[axis]));
    const direction = (u.q.x - u.p.x) * (v.q.x - v.p.x) + (u.q.y - u.p.y) * (v.q.y - v.p.y);
    // Coincident edges overlap only when their filled surfaces lie on the same side.
    return span > 1e-12 && direction * u.filledSide * v.filledSide > 0;
  }));
}

function ringError(ring: Point[]): string | null {
  if (ring.length < 3 || ring.length > 101) return "Each area boundary requires 3 to 101 vertices";
  if (ring.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return "Area coordinates must be finite";
  if (new Set(ring.map(p => `${p.x},${p.y}`)).size !== ring.length) return "Area boundaries cannot repeat vertices";
  if (polygonRingArea(ring) <= 1e-12) return "Area boundaries must enclose a positive area";
  for (let i = 0; i < ring.length; i++) {
    for (let j = i + 1; j < ring.length; j++) {
      if (j === i + 1 || (i === 0 && j === ring.length - 1)) continue;
      if (segmentsIntersect(ring[i], ring[(i + 1) % ring.length], ring[j], ring[(j + 1) % ring.length]))
        return "Area boundaries cannot intersect themselves";
    }
  }
  return null;
}

function ringsIntersect(a: Point[], b: Point[]): boolean {
  return a.some((p, i) => b.some((q, j) => segmentsIntersect(p, a[(i + 1) % a.length], q, b[(j + 1) % b.length])));
}

/** Holes must be strictly inside, disjoint, simple rings; orientation has no meaning. */
export function polygonTopologyError(geometry: Pick<PolygonGeometry, "vertices" | "holes">): string | null {
  const rings = polygonRings(geometry);
  if (rings.length > 51 || rings.reduce((sum, ring) => sum + ring.length, 0) > 500)
    return "Area measurements cannot exceed 50 cutouts or 500 total vertices";
  for (const ring of rings) {
    const error = ringError(ring);
    if (error) return error;
  }
  const [outer, ...holes] = rings;
  for (let index = 0; index < holes.length; index++) {
    const hole = holes[index];
    if (ringsIntersect(outer, hole) || !pointInPolygonRing(hole[0], outer))
      return "Cutouts must lie strictly inside the outer boundary";
    if (holes.slice(0, index).some(other => ringsIntersect(other, hole) ||
      pointInPolygonRing(hole[0], other) || pointInPolygonRing(other[0], hole)))
      return "Cutouts cannot overlap or contain each other";
  }
  return null;
}
